import { cn } from '#app/utils/misc.tsx'

/**
 * The score, rendered one way everywhere.
 *
 * It used to render four ways across four page types: a bordered accent box on
 * listings and rankings, a bare accent numeral on the homepage, an unstyled
 * bold cell in the comparison table, and a fifth treatment inside the ranking
 * table. A reader moving from /best/ to /compare/ saw the same measurement
 * change colour, typeface and weight, which reads as three different products
 * rather than one publication's number.
 *
 * Lives in its own module rather than in `business-listing.tsx` because the
 * comparison and ranking tables need it and are not listings.
 *
 * ## Density varies, identity does not
 *
 * Accent colour, monospace, tabular figures and one decimal are fixed at every
 * size. Only the surrounding furniture changes:
 *
 *   plain → bare numeral, for table cells and dense rows where a bordered box
 *           would fight the row rhythm
 *   sm    → boxed, for a listing row
 *   lg    → boxed and stretched, for a ranked entry
 */

/**
 * One decimal, always.
 *
 * Rounding to integers turns a real ordering into a wall of visual ties, and
 * varying the precision per page makes the same business look differently
 * measured depending on where you found it.
 */
export function formatScore(score: number) {
	return score.toFixed(1)
}

export function ScoreMark({
	score,
	rank,
	size = 'sm',
	className,
}: {
	score: number
	rank?: number
	/**
	 * `lg` is for a ranked entry, where the score is the reason the entry is
	 * where it is and should carry the same visual weight as the business name.
	 * At `sm` it was a chip beside a heading twice its size, which read as
	 * metadata about the name rather than the judgment the page exists to make.
	 *
	 * It stretches to its row so the block lines up with the name, the rating
	 * and the best-for tag as one unit.
	 *
	 * `plain` drops the box for table cells. It keeps the accent, the typeface
	 * and the decimal, so the number is still recognisably ours.
	 */
	size?: 'plain' | 'sm' | 'lg'
	className?: string
}) {
	/*
	 * The bare variant returns a span, not a div. These land inside <td> and
	 * inline flow, where a block-level box would break the row.
	 */
	if (size === 'plain') {
		return (
			<span
				className={cn(
					'text-accent font-mono font-medium tabular-nums',
					className,
				)}
			>
				{formatScore(score)}
			</span>
		)
	}

	const large = size === 'lg'
	return (
		<div
			className={cn(
				'border-accent/30 bg-accent/5 flex shrink-0 flex-col items-center justify-center rounded-lg border',
				/*
				 * min-width, never a fixed width. At a fixed 4.5rem the box was
				 * narrower than the text-3xl numeral it contained, so a score of
				 * 91.7 rendered hard against its own border. A floor keeps the
				 * column of scores aligned down the page while still letting the
				 * box grow if a value ever needs more room.
				 */
				large
					? 'min-w-[5.5rem] self-stretch px-4 py-3'
					: 'min-w-[3rem] px-2.5 py-1.5',
				className,
			)}
		>
			<span
				className={cn(
					'text-accent font-mono leading-none font-medium tabular-nums',
					large ? 'text-2xl' : 'text-base',
				)}
			>
				{formatScore(score)}
			</span>
			{rank != null ? (
				<span
					className={cn(
						'text-muted-foreground font-mono leading-none tabular-nums',
						large ? 'mt-2 text-xs' : 'mt-1 text-[10px]',
					)}
				>
					#{rank}
				</span>
			) : null}
		</div>
	)
}

/**
 * Star rating and how many people left one.
 *
 * Deliberately not accent-coloured. Reserving the accent for our own score
 * makes the palette mean something: muted is a business's own claim about
 * itself, ink is what we measured, accent is our number. Spending it on a
 * Google star rating would put our colour on someone else's assertion.
 */
export function RatingMark({
	ratingValue,
	ratingCount,
	className,
}: {
	ratingValue?: number | null
	ratingCount?: number | null
	className?: string
}) {
	if (ratingValue == null) return null
	return (
		<span
			className={cn(
				'text-muted-foreground font-mono text-xs tabular-nums',
				className,
			)}
		>
			{ratingValue.toFixed(1)}★
			{ratingCount ? ` (${ratingCount.toLocaleString()})` : ''}
		</span>
	)
}
